// PhotographyGallery.js - Photography Gallery section component with tabs
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed Gallery section with tabs and image grid
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The gallery elements
 */
export const createPhotographyGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'photography-gallery-section';
  const galleryContainerClass = 'photography-gallery-container';
  const galleryHeaderClass = 'photography-gallery-header';
  const galleryTitleClass = 'photography-gallery-title';
  const gallerySubtitleClass = 'photography-gallery-subtitle';
  const galleryContentClass = 'photography-gallery-content';
  const galleryTabsClass = 'photography-gallery-tabs';
  const galleryTabClass = 'photography-gallery-tab';
  const galleryTabActiveClass = 'photography-gallery-tab-active';
  const galleryTabsContentClass = 'photography-gallery-tabs-content';
  const galleryTabContentClass = 'photography-gallery-tab-content';
  const galleryTabContentActiveClass = 'photography-gallery-tab-content-active';
  const galleryGridClass = 'photography-gallery-grid';
  const galleryImageItemClass = 'photography-gallery-image-item';
  const galleryImageClass = 'photography-gallery-image';
  const galleryImageOverlayClass = 'photography-gallery-image-overlay';
  const galleryImageInfoClass = 'photography-gallery-image-info';
  const galleryImageTitleClass = 'photography-gallery-image-title';
  const galleryImageCategoryClass = 'photography-gallery-image-category';
  const galleryPatternOverlayClass = 'photography-gallery-pattern-overlay';
  const galleryViewAllButtonClass = 'photography-gallery-view-all-button';
  const galleryViewAllWrapperClass = 'photography-gallery-view-all-wrapper';

  // Desktop Styles (default) - Photography theme with elegant dark design
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f0f0f',
    background: `
      radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(212, 175, 55, 0.06) 0%, transparent 50%),
      linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 25%, #2d2d2d 50%, #1a1a1a 75%, #0f0f0f 100%)
    `,
    color: '#f5f5f5',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px, 120px 120px',
    backgroundPosition: '0 0, 60px 60px',
    zIndex: '0',
    opacity: '0.4'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const galleryTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#f5f5f5',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(245, 245, 245, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const galleryContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '40px'
  };

  const galleryTabsStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '60px',
    flexWrap: 'wrap',
    width: '100%'
  };

  const galleryTabStyles = {
    padding: '16px 32px',
    borderRadius: '0',
    fontSize: '16px',
    fontWeight: '400',
    color: 'rgba(245, 245, 245, 0.7)',
    backgroundColor: 'rgba(26, 26, 26, 0.6)',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden'
  };

  const galleryTabHoverStyles = {
    color: '#f5f5f5',
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    border: '1px solid rgba(212, 175, 55, 0.4)',
    transform: 'translateY(-2px)'
  };

  const galleryTabActiveStyles = {
    color: '#1a1a1a',
    backgroundColor: '#d4af37',
    border: '1px solid #d4af37',
    boxShadow: '0 8px 25px rgba(212, 175, 55, 0.3)',
    transform: 'translateY(-2px)'
  };

  const galleryTabsContentStyles = {
    width: '100%',
    position: 'relative'
  };

  const galleryTabContentStyles = {
    display: 'none',
    opacity: '0',
    transition: 'opacity 0.5s ease',
    width: '100%'
  };

  const galleryTabContentActiveStyles = {
    display: 'block',
    opacity: '1'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '20px'
  };

  const galleryImageItemStyles = {
    position: 'relative',
    height: '400px',
    borderRadius: '0',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(26, 26, 26, 0.8)',
    border: '1px solid rgba(212, 175, 55, 0.1)'
  };

  const galleryImageItemHoverStyles = {
    transform: 'translateY(-8px) scale(1.02)',
    border: '1px solid rgba(212, 175, 55, 0.4)',
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.6),
      0 0 25px rgba(212, 175, 55, 0.2)
    `
  };

  const galleryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'brightness(0.8) contrast(1.1)'
  };

  const galleryImageHoverStyles = {
    filter: 'brightness(0.6) contrast(1.2)',
    transform: 'scale(1.05)'
  };

  const galleryImageOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(212, 175, 55, 0.1) 0%, 
        rgba(26, 26, 26, 0.4) 40%, 
        rgba(15, 15, 15, 0.8) 100%
      )
    `,
    opacity: '0',
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  const galleryImageOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryImageInfoStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '30px 25px',
    zIndex: '2',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const galleryImageInfoHoverStyles = {
    transform: 'translateY(0)',
    opacity: '1'
  };

  const galleryImageTitleStyles = {
    fontSize: '22px',
    fontWeight: '400',
    color: '#f5f5f5',
    marginBottom: '8px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const galleryImageCategoryStyles = {
    fontSize: '12px',
    color: '#d4af37',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const galleryViewAllButtonStyles = {
    display: 'inline-block',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 32px',
    backgroundColor: 'transparent',
    border: '2px solid #d4af37',
    borderRadius: '0',
    color: '#d4af37',
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    textDecoration: 'none',
    position: 'relative',
    overflow: 'hidden',
    zIndex: '1'
  };

  const galleryViewAllButtonHoverStyles = {
    color: '#1a1a1a',
    backgroundColor: '#d4af37',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(212, 175, 55, 0.3)'
  };

  const galleryViewAllWrapperStyles = {
    textAlign: 'center',
    marginTop: '40px',
    width: '100%'
  };

  // Tablet Styles
  const galleryTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleTabletStyles = {
    fontSize: '48px'
  };

  const gallerySubtitleTabletStyles = {
    fontSize: '18px'
  };

  const galleryTabsTabletStyles = {
    gap: '10px'
  };

  const galleryTabTabletStyles = {
    padding: '14px 24px',
    fontSize: '14px'
  };

  const galleryGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const galleryImageItemTabletStyles = {
    height: '350px'
  };

  const galleryViewAllButtonTabletStyles = {
    padding: '14px 28px',
    fontSize: '13px'
  };

  // Mobile Styles
  const galleryMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleMobileStyles = {
    fontSize: '40px'
  };

  const gallerySubtitleMobileStyles = {
    fontSize: '16px'
  };

  const galleryTabsMobileStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px'
  };

  const galleryTabMobileStyles = {
    width: '100%',
    maxWidth: '280px',
    textAlign: 'center',
    padding: '14px 20px'
  };

  const galleryGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const galleryImageItemMobileStyles = {
    height: '300px'
  };

  const galleryImageInfoMobileStyles = {
    padding: '25px 20px'
  };

  const galleryImageTitleMobileStyles = {
    fontSize: '20px'
  };

  const galleryViewAllButtonMobileStyles = {
    padding: '14px 24px',
    fontSize: '12px',
    width: '100%',
    maxWidth: '280px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryPatternOverlayClass, galleryPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContentClass, galleryContentStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabsClass, galleryTabsStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabClass, galleryTabStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabActiveClass, galleryTabActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabsContentClass, galleryTabsContentStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabContentClass, galleryTabContentStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTabContentActiveClass, galleryTabContentActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageItemClass, galleryImageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageOverlayClass, galleryImageOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageInfoClass, galleryImageInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTitleClass, galleryImageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageCategoryClass, galleryImageCategoryStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryViewAllButtonClass, galleryViewAllButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryViewAllWrapperClass, galleryViewAllWrapperStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryTabClass}--hover`, galleryTabHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageItemClass}--hover`, galleryImageItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageClass}--hover`, galleryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageOverlayClass}--hover`, galleryImageOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageInfoClass}--hover`, galleryImageInfoHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryViewAllButtonClass}--hover`, galleryViewAllButtonHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(gallerySectionClass, galleryTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTitleClass, galleryTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(gallerySubtitleClass, gallerySubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTabsClass, galleryTabsTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTabClass, galleryTabTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryGridClass, galleryGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryImageItemClass, galleryImageItemTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryViewAllButtonClass, galleryViewAllButtonTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(gallerySectionClass, galleryMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, galleryTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(gallerySubtitleClass, gallerySubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTabsClass, galleryTabsMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTabClass, galleryTabMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, galleryGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageItemClass, galleryImageItemMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageInfoClass, galleryImageInfoMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageTitleClass, galleryImageTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryViewAllButtonClass, galleryViewAllButtonMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryPatternOverlayClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryContentClass,
    galleryTabsClass,
    galleryTabClass,
    galleryTabActiveClass,
    galleryTabsContentClass,
    galleryTabContentClass,
    galleryTabContentActiveClass,
    galleryGridClass,
    galleryImageItemClass,
    galleryImageClass,
    galleryImageOverlayClass,
    galleryImageInfoClass,
    galleryImageTitleClass,
    galleryImageCategoryClass,
    galleryViewAllButtonClass,
    galleryViewAllWrapperClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const contentId = Date.now() + 6;
  
  // Tabs
  const tabsId = Date.now() + 7;
  const tab1Id = Date.now() + 8;
  const tab2Id = Date.now() + 9;
  const tab3Id = Date.now() + 10;
  const tab4Id = Date.now() + 11;
  
  // Tabs content
  const tabsContentId = Date.now() + 12;

  // Gallery data for different categories
  const galleryCategories = [
    {
      id: 'weddings',
      name: 'Weddings',
      linkTo: 'wedding-photography',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop&crop=center',
          title: 'Romantic Garden Wedding',
          category: 'Ceremony'
        },
        {
          src: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6??w=600&h=400&fit=crop&crop=center',
          title: 'First Dance Magic',
          category: 'Reception'
        },
        {
          src: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=600&h=400&fit=crop&crop=center',
          title: 'Bridal Portrait',
          category: 'Portraits'
        },
        {
          src: 'https://images.unsplash.com/photo-1529634597503-139d3726fed5?w=600&h=400&fit=crop&crop=center',
          title: 'Wedding Details',
          category: 'Details'
        },
        {
          src: 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=600&h=400&fit=crop&crop=center',
          title: 'Couple\'s Joy',
          category: 'Portraits'
        },
        {
          src: 'https://images.unsplash.com/photo-1520854221256-17451cc331bf?w=600&h=400&fit=crop&crop=center',
          title: 'Wedding Celebration',
          category: 'Reception'
        }
      ]
    },
    {
      id: 'portraits',
      name: 'Portraits',
      linkTo: 'portrait-sessions',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop&crop=center',
          title: 'Professional Headshot',
          category: 'Business'
        },
        {
          src: 'https://images.unsplash.com/photo-1597113117918-eb40fe0b84e7?w=600&h=400&fit=crop&crop=center',
          title: 'Natural Beauty',
          category: 'Lifestyle'
        },
        {
          src: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=600&h=400&fit=crop&crop=center',
          title: 'Creative Portrait',
          category: 'Artistic'
        },
        {
          src: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=400&fit=crop&crop=center',
          title: 'Executive Portrait',
          category: 'Business'
        },
        {
          src: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=400&fit=crop&crop=center',
          title: 'Elegant Portrait',
          category: 'Lifestyle'
        },
        {
          src: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=400&fit=crop&crop=center',
          title: 'Character Study',
          category: 'Artistic'
        }
      ]
    },
    {
      id: 'events',
      name: 'Events',
      linkTo: 'event-photography',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&h=400&fit=crop&crop=center',
          title: 'Corporate Gala',
          category: 'Corporate'
        },
        {
          src: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&h=400&fit=crop&crop=center',
          title: 'Birthday Celebration',
          category: 'Private'
        },
        {
          src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&h=400&fit=crop&crop=center',
          title: 'Conference Speaker',
          category: 'Corporate'
        },
        {
          src: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&h=400&fit=crop&crop=center',
          title: 'Anniversary Party',
          category: 'Private'
        },
        {
          src: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=600&h=400&fit=crop&crop=center',
          title: 'Product Launch',
          category: 'Corporate'
        },
        {
          src: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&h=400&fit=crop&crop=center',
          title: 'Family Gathering',
          category: 'Private'
        }
      ]
    },
    {
      id: 'commercial',
      name: 'Commercial',
      linkTo: 'commercial-work',
      images: [
        {
          src: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop&crop=center',
          title: 'Office Space',
          category: 'Architecture'
        },
        {
          src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&h=400&fit=crop&crop=center',
          title: 'Restaurant Ambiance',
          category: 'Hospitality'
        },
        {
          src: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop&crop=center',
          title: 'Retail Store',
          category: 'Retail'
        },
        {
          src: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop&crop=center',
          title: 'Modern Interior',
          category: 'Architecture'
        },
        {
          src: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&h=400&fit=crop&crop=center',
          title: 'Hotel Lobby',
          category: 'Hospitality'
        },
        {
          src: 'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=600&h=400&fit=crop&crop=center',
          title: 'Boutique Display',
          category: 'Retail'
        }
      ]
    }
  ];

  // Create tab content elements
  let currentId = tabsContentId + 1;
  const tabContentElements = [];

  galleryCategories.forEach((category, categoryIndex) => {
    const tabContentId = currentId++;
    const gridId = currentId++;
    const viewAllWrapperID = currentId++;
    const viewAllButtonId = currentId++;

    // Create image elements for this category
    const imageElements = [];
    const imageIds = [];

    category.images.forEach((image, imageIndex) => {
      const itemId = currentId++;
      const imageId = currentId++;
      const overlayId = currentId++;
      const infoId = currentId++;
      const titleId = currentId++;
      const categoryId = currentId++;

      imageIds.push(itemId);

      imageElements.push(
        // Image Item Container
        {
          id: itemId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'div',
          name: `Gallery Image Item ${imageIndex + 1}`,
          parentId: gridId,
          hierarchyName: getHierarchyName('div'),
          className: galleryImageItemClass,
          styles: {},
          children: [imageId, overlayId, infoId],
          text: '',
          attributes: {
            'data-element': 'gallery-image-item',
            'data-category': category.id
          }
        },
        // Image
        {
          id: imageId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'image',
          name: `Gallery Image ${imageIndex + 1}`,
          parentId: itemId,
          hierarchyName: getHierarchyName('image'),
          className: galleryImageClass,
          styles: {},
          src: image.src,
          attributes: {
            'data-element': 'gallery-image',
            'alt': image.title
          }
        },
        // Overlay
        {
          id: overlayId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'div',
          name: `Gallery Image Overlay ${imageIndex + 1}`,
          parentId: itemId,
          hierarchyName: getHierarchyName('div'),
          className: galleryImageOverlayClass,
          styles: {},
          children: [],
          text: '',
          attributes: {
            'data-element': 'gallery-image-overlay'
          }
        },
        // Info
        {
          id: infoId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'div',
          name: `Gallery Image Info ${imageIndex + 1}`,
          parentId: itemId,
          hierarchyName: getHierarchyName('div'),
          className: galleryImageInfoClass,
          styles: {},
          children: [titleId, categoryId],
          text: '',
          attributes: {
            'data-element': 'gallery-image-info'
          }
        },
        // Title
        {
          id: titleId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'h4',
          name: `Gallery Image Title ${imageIndex + 1}`,
          parentId: infoId,
          hierarchyName: getHierarchyName('h4'),
          className: galleryImageTitleClass,
          styles: {},
          children: [],
          text: image.title,
          attributes: {
            'data-element': 'gallery-image-title'
          }
        },
        // Category
        {
          id: categoryId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'span',
          name: `Gallery Image Category ${imageIndex + 1}`,
          parentId: infoId,
          hierarchyName: getHierarchyName('span'),
          className: galleryImageCategoryClass,
          styles: {},
          children: [],
          text: image.category,
          attributes: {
            'data-element': 'gallery-image-category'
          }
        }
      );
    });

    // Add tab content and grid
    tabContentElements.push(
      // Tab Content
      {
        id: tabContentId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${category.name} Tab Content`,
        parentId: tabsContentId,
        hierarchyName: getHierarchyName('div'),
        className: categoryIndex === 0 ? `${galleryTabContentClass} ${galleryTabContentActiveClass}` : galleryTabContentClass,
        styles: {},
        children: [gridId, viewAllWrapperID],
        text: '',
        attributes: {
          'data-element': 'gallery-tab-content',
          'data-tab-content': `tab-${categoryIndex + 1}`,
          'data-active': categoryIndex === 0 ? 'true' : 'false'
        }
      },
      // Grid
      {
        id: gridId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${category.name} Gallery Grid`,
        parentId: tabContentId,
        hierarchyName: getHierarchyName('div'),
        className: galleryGridClass,
        styles: {},
        children: imageIds,
        text: '',
        attributes: {
          'data-element': 'gallery-grid'
        }
      },
      // View All Wrapper
      {
        id: viewAllWrapperID,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${category.name} View All Wrapper`,
        parentId: tabContentId,
        hierarchyName: getHierarchyName('div'),
        className: galleryViewAllWrapperClass,
        styles: {},
        children: [viewAllButtonId],
        text: '',
        attributes: {
          'data-element': 'gallery-view-all-wrapper'
        }
      },
      // View All Button
      {
        id: viewAllButtonId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'button',
        name: `${category.name} View All Button`,
        parentId: viewAllWrapperID,
        hierarchyName: getHierarchyName('button'),
        className: galleryViewAllButtonClass,
        styles: {},
        children: [],
        text: `View All ${category.name}`,
        linkTo: category.linkTo,
        attributes: {
          'data-element': 'gallery-view-all-button',
          'data-category': category.id
        }
      },
      ...imageElements
    );
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'gallery-section',
        'data-component': 'section',
        'data-section-type': 'gallery',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'gallery-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'gallery-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'gallery-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Our Photography Gallery',
      attributes: {
        'data-element': 'gallery-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Explore our diverse portfolio showcasing the artistry and emotion captured in every frame. From intimate moments to grand celebrations, each image tells a unique story.',
      attributes: {
        'data-element': 'gallery-subtitle'
      }
    },
    // Content container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContentClass,
      styles: {},
      children: [tabsId, tabsContentId],
      text: '',
      attributes: {
        'data-element': 'gallery-content'
      }
    },
    // Tabs
    {
      id: tabsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Tabs',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: galleryTabsClass,
      styles: {},
      children: [tab1Id, tab2Id, tab3Id, tab4Id],
      text: '',
      attributes: {
        'data-element': 'gallery-tabs'
      }
    },
    // Tab 1 - Weddings
    {
      id: tab1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Weddings Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: `${galleryTabClass} ${galleryTabActiveClass}`,
      styles: {},
      children: [],
      text: 'Weddings',
      attributes: {
        'data-element': 'gallery-tab',
        'data-tab': 'tab-1',
        'data-active': 'true'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1000,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: galleryTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: galleryTabContentActiveClass
            },
            label: `Tab Click: ${galleryTabActiveClass} & ${galleryTabContentActiveClass}`
          }
        ]
      }
    },
    // Tab 2 - Portraits
    {
      id: tab2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Portraits Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: galleryTabClass,
      styles: {},
      children: [],
      text: 'Portraits',
      attributes: {
        'data-element': 'gallery-tab',
        'data-tab': 'tab-2',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1001,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: galleryTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: galleryTabContentActiveClass
            },
            label: `Tab Click: ${galleryTabActiveClass} & ${galleryTabContentActiveClass}`
          }
        ]
      }
    },
    // Tab 3 - Events
    {
      id: tab3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Events Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: galleryTabClass,
      styles: {},
      children: [],
      text: 'Events',
      attributes: {
        'data-element': 'gallery-tab',
        'data-tab': 'tab-3',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1002,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: galleryTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: galleryTabContentActiveClass
            },
            label: `Tab Click: ${galleryTabActiveClass} & ${galleryTabContentActiveClass}`
          }
        ]
      }
    },
    // Tab 4 - Commercial
    {
      id: tab4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Commercial Tab',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: galleryTabClass,
      styles: {},
      children: [],
      text: 'Commercial',
      attributes: {
        'data-element': 'gallery-tab',
        'data-tab': 'tab-4',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1003,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: galleryTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: galleryTabContentActiveClass
            },
            label: `Tab Click: ${galleryTabActiveClass} & ${galleryTabContentActiveClass}`
          }
        ]
      }
    },
    // Tabs content container
    {
      id: tabsContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gallery Tabs Content',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: galleryTabsContentClass,
      styles: {},
      children: tabContentElements.filter(el => el.parentId === tabsContentId).map(el => el.id),
      text: '',
      attributes: {
        'data-element': 'gallery-tabs-content'
      }
    },
    ...tabContentElements
  ];

  return {
    gallerySection: newElements[0],
    elements: newElements
  };
}; 